import LocalPharmacy from '@mui/icons-material/LocalPharmacy'
import People from '@mui/icons-material/People'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Settings from '@mui/icons-material/Settings'
import Business from '@mui/icons-material/Business'
import Assignment from '@mui/icons-material/Assignment'
import Healing from '@mui/icons-material/Healing'
import Warning from '@mui/icons-material/Warning'
import LocationOn from '@mui/icons-material/LocationOn'
import CheckCircle from '@mui/icons-material/CheckCircle'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
  Alert,
} from '@mui/material'
import { useContext, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { GenericHeaderBackButton } from 'src/consumer/components/UserActivityPage/MyPlanComponents'
import { ROLE_TYPES } from 'src/consumer/constants'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import ClinicHeader from '../ClinicHeader'
import { HeaderGreeting } from '../ClinicHeader/ClinicHeaderStyles'
import { AvailabilityPageContainer } from '../ClinicManageBookingsPage/ClinicManageBookingsPageStyles'

// Styled components
const PageContainer = styled(AvailabilityPageContainer)`
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  min-height: 100vh;
  padding-bottom: 40px;
`

const ContentWrapper = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const CommonSectionCard = styled(Card)`
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(116, 80, 170, 0.1);
  margin-bottom: 32px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 8px 32px rgba(116, 80, 170, 0.15);
    transform: translateY(-2px);
  }
`

const SectionHeader = styled(Box)`
  padding: 24px 32px;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%);
  border-bottom: 1px solid rgba(116, 80, 170, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const SectionTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const SectionContent = styled(Box)`
  padding: 32px;
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CommonActionCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid rgba(116, 80, 170, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  overflow: hidden;
  position: relative;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7450aa 0%, #5a3d88 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(116, 80, 170, 0.4);
    box-shadow: 0 8px 24px rgba(116, 80, 170, 0.2);
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }
`

const ActionCardContent = styled(CardContent)`
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
`

const ActionIcon = styled(Box)`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%);
  margin-bottom: 8px;
`

const ActionTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const ActionDescription = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '2px solid rgba(116, 80, 170, 0.1)',
  marginBottom: 24,
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '15px',
    fontWeight: 600,
    color: '#6b7280',
    padding: '12px 24px',
    minHeight: 48,
    '&.Mui-selected': {
      color: '#7450aa',
    },
  },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    background: 'linear-gradient(90deg, #7450aa 0%, #5a3d88 100%)',
  },
}))

const LocationActionCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid rgba(16, 185, 129, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  overflow: hidden;
  position: relative;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.2);
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }
`

const LocationIcon = styled(Box)`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%);
  margin-bottom: 8px;
`

export default function ClinicHomePage() {
  const navigate = useNavigate()
  const { clinicBrandId, clinicId } = useParams()
  const { chosenRoleFlow, ownerData, assistantData } = useContext(ClinicDataContext)

  // Get all clinic locations for this brand
  const allClinicLocations = useMemo(() => {
    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      return (ownerData?.associatedClinicsData || []).filter(
        (item) => item?.clinicBrandId === clinicBrandId,
      )
    } else if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) {
      return (assistantData?.associatedClinicsData || []).filter(
        (item) => item?.clinicBrandId === clinicBrandId,
      )
    }
    return []
  }, [chosenRoleFlow, ownerData, assistantData, clinicBrandId])

  // If there's a clinicId in URL, find its index for the tab
  const currentTabIndex = useMemo(() => {
    if (clinicId) {
      const index = allClinicLocations.findIndex((item) => item.id === clinicId)
      return index >= 0 ? index : 0
    }
    return 0
  }, [clinicId, allClinicLocations])

  // Get current clinic data
  const currentClinicData = useMemo(() => {
    if (clinicId) {
      return allClinicLocations.find((item) => item.id === clinicId)
    }
    return allClinicLocations[currentTabIndex] || allClinicLocations[0]
  }, [clinicId, allClinicLocations, currentTabIndex])

  const handleTabChange = (event, newValue) => {
    const selectedClinic = allClinicLocations[newValue]
    if (selectedClinic) {
      navigate(`../${selectedClinic.id}`, { replace: true })
    }
  }

  // Common actions available across all locations
  const commonActions = useMemo(() => {
    const actions = []
    const isApproved = currentClinicData?.kycStatus === 'approved'

    if (
      (chosenRoleFlow === ROLE_TYPES.roleClinicOwner ||
        chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) &&
      isApproved
    ) {
      actions.push({
        id: 'pharmacy-database',
        title: 'Pharmacy Database',
        description: 'Manage and search your pharmacy inventory and medications',
        icon: LocalPharmacy,
        color: '#7450aa',
        onClick: () => navigate('pharmacy-database'),
      })
    }

    if (
      (chosenRoleFlow === ROLE_TYPES.roleClinicOwner ||
        chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) &&
      isApproved
    ) {
      actions.push({
        id: 'patient-list',
        title: 'Patient Listing',
        description: 'View and manage all patients across your clinic locations',
        icon: People,
        color: '#10b981',
        onClick: () => navigate('patient-list'),
      })
    }

    return actions
  }, [chosenRoleFlow, currentClinicData, navigate])

  // Location-specific actions for the current clinic
  const locationActions = useMemo(() => {
    if (!currentClinicData) return []
    
    const actions = []
    const isApproved = currentClinicData?.kycStatus === 'approved'

    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      actions.push({
        id: 'schedule',
        title: 'Location Schedule',
        description: 'Manage schedules and availability for this location',
        icon: CalendarToday,
        onClick: () => navigate(`schedule`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) {
      actions.push({
        id: 'manage-bookings',
        title: 'Manage Bookings',
        description: 'View and manage appointments for this location',
        icon: CalendarToday,
        onClick: () => navigate(`manage-bookings`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) {
      actions.push({
        id: 'planned-appointments',
        title: 'Upcoming Appointments',
        description: 'View your upcoming appointments',
        icon: Assignment,
        onClick: () => navigate(`planned-appointments`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant && isApproved) {
      actions.push({
        id: 'communications',
        title: 'Communications',
        description: 'Manage patient communications (BETA)',
        icon: Healing,
        onClick: () => navigate(`communications`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      actions.push({
        id: 'update-location-details',
        title: 'Location Settings',
        description: 'Update location information and details',
        icon: Settings,
        onClick: () => navigate(`update-location-details`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      actions.push({
        id: 'manage-workforce',
        title: 'Location Staff',
        description: 'Manage staff members for this location',
        icon: Business,
        onClick: () => navigate(`manage-workforce`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      actions.push({
        id: 'assistant-responsibility',
        title: 'Assistant Responsibilities',
        description: 'Configure assistant roles and permissions',
        icon: Assignment,
        onClick: () => navigate(`assistant-responsibility`),
      })
    }

    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      actions.push({
        id: 'clinic-services',
        title: 'Clinic Services',
        description: 'Manage services offered at this location',
        icon: Healing,
        onClick: () => navigate(`clinic-services`),
      })
    }

    return actions
  }, [currentClinicData, chosenRoleFlow, navigate])

  if (!currentClinicData && allClinicLocations.length === 0) {
    return (
      <PageContainer>
        <ClinicHeader heading="No Clinic Locations" />
        <ContentWrapper>
          <Alert severity="info">No clinic locations found for this brand.</Alert>
        </ContentWrapper>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <ClinicHeader
        component={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <GenericHeaderBackButton
              clickfunction={() => navigate(`../${clinicBrandId}`)}
              styles={{ margin: '0' }}
            />
            <HeaderGreeting main>{currentClinicData?.name || 'Clinic Location'}</HeaderGreeting>
          </Box>
        }
      />

      {currentClinicData?.kycStatus !== 'approved' && (
        <ContentWrapper>
          <Alert
            severity="warning"
            icon={<Warning />}
            sx={{
              mb: 3,
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                color: '#f59e0b',
              },
            }}
          >
            Your KYC verification is pending. Some features may be limited until approval.
          </Alert>
        </ContentWrapper>
      )}

      <ContentWrapper>
        {/* Common Actions Section */}
        {commonActions.length > 0 && (
          <CommonSectionCard>
            <SectionHeader>
              <SectionTitle>
                <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                Common Resources
              </SectionTitle>
            </SectionHeader>
            <SectionContent>
              <Grid container spacing={3}>
                {commonActions.map((action) => {
                  const IconComponent = action.icon
                  return (
                    <Grid item xs={12} sm={6} md={4} key={action.id}>
                      <CommonActionCard onClick={action.onClick}>
                        <ActionCardContent>
                          <ActionIcon>
                            <IconComponent sx={{ fontSize: 28, color: action.color || '#7450aa' }} />
                          </ActionIcon>
                          <ActionTitle>{action.title}</ActionTitle>
                          <ActionDescription>{action.description}</ActionDescription>
                        </ActionCardContent>
                      </CommonActionCard>
                    </Grid>
                  )
                })}
              </Grid>
            </SectionContent>
          </CommonSectionCard>
        )}

        {/* Location Tabs Section */}
        {allClinicLocations.length > 1 && (
          <CommonSectionCard>
            <SectionHeader>
              <SectionTitle>
                <LocationOn sx={{ color: '#7450aa', fontSize: 24 }} />
                Clinic Locations
              </SectionTitle>
            </SectionHeader>
            <Box sx={{ px: 3, pt: 2 }}>
              <StyledTabs
                value={currentTabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {allClinicLocations.map((clinic, index) => (
                  <Tab
                    key={clinic.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {clinic.name}
                        </Typography>
                        {clinic.kycStatus === 'pending' && (
                          <Chip
                            label="KYC Pending"
                            color="warning"
                            size="small"
                            sx={{ height: 20, fontSize: '10px' }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </StyledTabs>
            </Box>
          </CommonSectionCard>
        )}

        {/* Location-Specific Actions */}
        <CommonSectionCard>
          <SectionHeader>
            <SectionTitle>
              <LocationOn sx={{ color: '#10b981', fontSize: 24 }} />
              {currentClinicData?.name} - Location Features
            </SectionTitle>
          </SectionHeader>
          <SectionContent>
            {locationActions.length > 0 ? (
              <Grid container spacing={3}>
                {locationActions.map((action) => {
                  const IconComponent = action.icon
                  return (
                    <Grid item xs={12} sm={6} md={4} key={action.id}>
                      <LocationActionCard onClick={action.onClick}>
                        <ActionCardContent>
                          <LocationIcon>
                            <IconComponent sx={{ fontSize: 28, color: '#10b981' }} />
                          </LocationIcon>
                          <ActionTitle>{action.title}</ActionTitle>
                          <ActionDescription>{action.description}</ActionDescription>
                        </ActionCardContent>
                      </LocationActionCard>
                    </Grid>
                  )
                })}
              </Grid>
            ) : (
              <Alert severity="info">No actions available for this location.</Alert>
            )}
          </SectionContent>
        </CommonSectionCard>
      </ContentWrapper>
    </PageContainer>
  )
}
